import { Component, Input, OnInit, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SearchStateService } from '../../../shared/services/search-state.service';
import { TranslateService } from '@ngx-translate/core';
import { GetCareNowFormatService } from '../../../../get-care-now-feature/utilities/getcarenow-format.service';
import { MCAppGatewayService } from '../../../../shared/services/mcappgateway/mcappgateway.service';
import { PostGetProviderDetailsRequest } from '../../../../shared/model/providerlocationsearch/PostGetProviderDetailRequest';
import { environment } from '../../../../../environments/environment';
import { ProviderSearchFilters } from '../../../../shared/model/providerlocationsearch/GetProviderSearchFilterResponse';
import { LocationSearchFilters } from '../../../../shared/model/providerlocationsearch/GetLocationSearchFilterResponse';

interface Provider {
  name: string;
  specialty: string;
  photoUrl: string;
  nextAvailable: string;
}

@Component({
  selector: 'app-location-search-details-mobile',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './location-search-details.component.html',
  styleUrls: ['./location-search-details.component.css']
})
export class LocationSearchDetailsComponent implements OnInit {
  @Input() isPlus: boolean = false;
  @Input() providerDetails: any | null = null;
  @Input() providerDetailsLocation: any | null = null;
  
  searchState = inject(SearchStateService);
  mcAppGateway = inject(MCAppGatewayService);
  private getCareNowFormatService: GetCareNowFormatService;
  
  // Location information - will be set from state observable
  eptID = '';
  location: any = null;
  photo: string = '';
  locationName: string = '';
  distance: string = '';
  addressLine1: string = '';
  addressLine2: string = '';
  phone: string = '';
  directions: string = '';
  openHours: any[] = [];
  isUrgentCare: boolean = false
  public waitTime: string = '';

  currentProviderFilters: ProviderSearchFilters | null = null;
  currentLocationFilters: LocationSearchFilters | null = null;
  queryCityZip: string = "";
  providerCityZip: string = "";

  // Breadcrumb translations
  breadcrumb1: string = 'Location Search';
  breadcrumb2: string = 'Results';

  provBreadcrumb1: string = "Provider Search"
  provBreadcrumb2: string = "Results";
  provName: string = "";
  provLocationName: string = ""

  getDirectionsText: string = 'Get directions';
  milesAwayText: string = 'mi away';
  asSoonAsText = "Book as soon as"
  callBtnText = "Call to Schedule"
  scheduleBtnText = "Schedule";

  reserveBtnText = "Reserve My Spot";
  providersLabel = "Providers";

  locale: string = '';
  
  
  // Providers list (for non-urgent care)
  providers: any[] = [];

  constructor(
    private translate: TranslateService
  ) {
    this.locale = sessionStorage.getItem('locale') || 'en-US';
    this.getCareNowFormatService = new GetCareNowFormatService(this.translate);
  }

  ngOnInit(): void {
    this.eptID = sessionStorage.getItem("eptID") || '';

    this.translate.use(this.locale);

    // Load translations
    this.translate.get('LOCATION_SEARCH').subscribe((translated: string) => {
      this.breadcrumb1 = translated;
    });

    this.translate.get('PROVIDER_SEARCH').subscribe((translated: string) => {
      this.provBreadcrumb1 = translated;
    });

    this.translate.get('RESULTS').subscribe((translated: string) => {
      this.breadcrumb2 = translated;
      this.provBreadcrumb2 = translated;
    });

    this.translate.get('GET_DIRECTIONS').subscribe((translated: string) => {
      this.getDirectionsText = translated;
    });

    this.translate.get('MILES_AWAY').subscribe((translated: string) => {
      this.milesAwayText = translated;
    });

    this.translate.get('BOOK_AS_SOON_AS').subscribe((translated: string) => {
      this.asSoonAsText = translated;
    });

    this.translate.get('SCHEDULE').subscribe((translated: string) => {
      this.scheduleBtnText = translated;
    });

    this.translate.get('CALL_TO_SCHEDULE').subscribe((translated: string) => {
      this.callBtnText = translated;
    });

    this.translate.get('RESERVE_MY_SPOT').subscribe((translated: string) => {
      this.reserveBtnText = translated;
    });

    this.translate.get('LOCATION_PROFILE.PROVIDERS').subscribe((translated: string) => {
      this.providersLabel = translated;
    });

    // this.translate.get('LOCATION').subscribe((translated: string) => {
    //   this.loca = translated;
    // });

    // Subscribe to location details from state
    this.searchState.locationDetails$.subscribe(location => {
      if (location) {
        this.location = location;
        this.loadLocationData(location);
      }
    });

    this.searchState.locationSearchFilters$.subscribe(filters => {
      if (filters) {
        this.currentLocationFilters = filters;
      }
    });

    this.searchState.providerSearchFilters$.subscribe(filters => {
      if (filters) {
        this.currentProviderFilters = filters;
      }
    });

    this.searchState.locationSearchQuery$.subscribe(query => {
      if(query) {
        this.queryCityZip = query.cityOrZip;
      }
    });

    this.searchState.providerSearchQuery$.subscribe(query => {
      if(query) {
        this.providerCityZip = query.cityOrZip;
      }
    });

    if (this.providerDetailsLocation) {
      this.loadLocationData(this.providerDetailsLocation);
    }

  }

  private loadLocationData(location: any): void {
    if (!location) return;

    this.photo = location.photo;

    this.locationName = location.locationName || location.name || '';

    if(this.distance == '') {
      this.distance = location.distanceInMiles ? location.distanceInMiles.toFixed(1) + this.milesAwayText : '';
    }

    // assume only urgent cares have an EMR id
    this.isUrgentCare = location.locationEmrId != null && location.locationEmrId != '';
    console.log("isUrgentCare:", this.isUrgentCare);

    this.openHours = location.locationOpenHours;
    
    // Handle address fields
    this.addressLine1 = location.address1 || '';
    if (location.address2) {
      this.addressLine1 = `${this.addressLine1}, ${location.address2}`;
    }
    
    const cityParts = [];
    if (location.city) cityParts.push(location.city);
    if (location.state) cityParts.push(location.state);
    if (location.zip) cityParts.push(location.zip);
    this.addressLine2 = cityParts.join(', ');
    
    this.phone = location.phone || '';
    
    // Determine if urgent care based on location type
    if (location.type) {
      this.isUrgentCare = location.type.toLowerCase().includes('urgent');
    }

    // Set providers if available
    if (location.providers && Array.isArray(location.providers)) {
      this.providers = location.providers;

      this.providers = this.providers.map(provider => ({
        ...provider,
        nextAvailability: provider.nextAvailability 
          ? this.getCareNowFormatService.formatNextAvailability(provider.nextAvailability)
          : null
      }));
    }
  }

  getUniqueHourTypes(): string[] {
    if (!this.openHours || this.openHours.length === 0) return [];
    
    const types = new Set<string>();
    this.openHours.forEach(hour => {
      if (hour.type) {
        types.add(hour.type);
      }
    });
    return Array.from(types);
  }

  getHoursByType(type: string): any[] {
    if (!this.openHours) return [];
    return this.openHours.filter(hour => hour.type === type);
  }

  // LocationDetails Breadcrumb

  clearLocationSearch(): void {
    this.searchState.setLocationSearchQuery({
      facilityOrLocationName: '',
      cityOrZip: this.queryCityZip
    });
    this.searchState.clearLocationSearchResults();
    this.clearLocationFilters();
    this.searchState.clearCurrentLocationDetails();
  }

  clearLocationFilters() {
    if (this.currentLocationFilters) {
      // Clear all selections
      this.currentLocationFilters.serviceList?.forEach(option => option.isSelected = false);
      this.currentLocationFilters.typeList?.forEach(option => option.isSelected = false);
 
      this.searchState.setLocationSearchFilters(this.currentLocationFilters);
    }
  }

  returnToSearchResults(): void {
    this.searchState.clearCurrentLocationDetails();
  }

  // ProviderDetailsLocation Breadcrumb
  clearProviderSearch(): void {
    // this.searchState.clearProviderSearchQuery();
    this.searchState.setProviderSearchQuery({
      nameOrSpecialty: '',
      cityOrZip: this.providerCityZip
    });
    this.searchState.clearProviderSearchResults();
    this.clearProviderFilters();
    // this.searchState.clearProviderSearchFilters();
    this.searchState.clearCurrentProviderDetails();
    this.searchState.clearCurrentProviderDetailsLocation();
  }

  clearProviderFilters() {
    if (this.currentProviderFilters) {
      // Clear all selections
      this.currentProviderFilters.specialtyList?.forEach(option => option.isSelected = false);
      this.currentProviderFilters.genderList?.forEach(option => option.isSelected = false);
      this.currentProviderFilters.affiliationList?.forEach(option => option.isSelected = false);
      this.currentProviderFilters.languageList?.forEach(option => option.isSelected = false);
      this.currentProviderFilters.onlineSchedulingList?.forEach(option => option.isSelected = false);

      this.searchState.setProviderSearchFilters(this.currentProviderFilters);
    }
  }

  backToProviderSearchResults(): void {
    this.searchState.clearCurrentProviderDetails();
    this.searchState.clearCurrentProviderDetailsLocation();
  }

  backToProviderDetails(): void {
    this.searchState.clearCurrentProviderDetailsLocation();
  }

  

  getDirections(event: Event): void {
    event.preventDefault();
    // Use locationDirection from API if available, otherwise construct URL
    if (this.location?.locationDirection) {
      window.open(this.location.locationDirection, '_blank');
    } else {
      const address = `${this.addressLine1}, ${this.addressLine2}`;
      const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
      window.open(url, '_blank');
    }
  }

   onCallPhone(provider: any): void {
    if (provider && provider.phone) {
      var phoneNumber = provider.phone.replace(/\D/g, ''); // Remove non-numeric characters
      console.log('Calling provider phone number:', phoneNumber);
      window.location.href = `tel:${phoneNumber}`;
    } else if(this.location?.phone){
      var phoneNumber = this.location.phone.replace(/\D/g, ''); // Remove non-numeric characters
      console.log('Calling location phone number:', phoneNumber);
      window.location.href = `tel:${phoneNumber}`;
    }
  }

  onReserveMySpot() {
    const isMobile = window.location.href.includes('mobile');
    var deepLink = "";
    
    if(this.location.locationEmrId == null || this.location.locationEmrId == null) {
      console.log("Reservation Error: no Location Emr ID");
      return;
    }

    if (this.eptID == null || this.eptID == undefined || this.eptID == "") {
      this.reserveNowUnAuth(isMobile);
    }
    else {
      this.reserveNowAuth();
    }
  }

  reserveNowUnAuth(isMobile: boolean) {
    // Logic to show more options or details for unauthenticated users
    var deepLink = isMobile ? environment.mobile_omw_deep_link_base_url : environment.web_unauth_omw_deep_link_base_url;
    deepLink += this.location.locationEmrId;

    // this.monitoringService.logEvent('Schedule/NoAuth', { feature: 'get-care-now', subfeature: 'IPUC', platform: isMobile ? 'mobile-plus' : 'web' }); // log as web event

    console.log("reserve now clicked (IPUC)");
    console.log(deepLink);

    window.location.href = deepLink;
  }

  reserveNowAuth() {
    var deepLink = '';
    const isPlus = sessionStorage.getItem("isPlus") != null ? sessionStorage.getItem("isPlus") === 'true'  : null;

    if(isPlus == null) {
      deepLink = environment.mobile_omw_deep_link_base_url;
      // this.monitoringService.logEvent('Schedule', { feature: 'get-care-now', subfeature: 'IPUC', platform: 'web' }); // log as web event
    } 
    else {
      deepLink = isPlus
          ? environment.mobile_omw_deep_link_base_url // For Plus users, use the mobile deep link
          : environment.epic_omw_deep_link_base_url; //  For non-Plus users, use the epic deep link
    
      var mobilePlatform = isPlus ? 'mobile-plus' : 'mobile-standard';
      // this.monitoringService.logEvent('Schedule', { feature: 'get-care-now', subfeature: 'IPUC', platform: mobilePlatform });
    }
   
    // add provider id and department id params to the deep link
    deepLink += this.location.locationEmrId;
    
    console.log("reserve now clicked (IPUC)");
    console.log(deepLink);

    window.location.href = deepLink;
  }

  onScheduleButtonClicked(provider: any) {
      const isMobileLaunch = window.location.href.includes('mobile');
  
      if (provider && provider.providerEmrId && provider.providerEmrId.length > 0) {
  
        if (this.eptID != null && this.eptID != ""){
          this.scheduleAuth(this.isPlus, provider);
        }
        else {
          this.scheduleUnAuth(isMobileLaunch, provider);  
        }
      }
    }
  
    scheduleAuth(isPlus: boolean | null, provider: any) {
      var deepLink = ''; 
  
      if (isPlus == null) {
        deepLink = environment.web_scheduling_deep_link_base_url;
      }
      else {
         deepLink = isPlus
          ? environment.mobile_scheduling_deep_link_base_url // For Plus users, use the mobile deep link
          : environment.epic_scheduling_deep_link_base_url; //  For non-Plus users, use the epic deep link
      }
  
      deepLink += "?workflow=NewProvider"
  
      deepLink += "&selProvId=" + provider.providerEmrId; 
        
      if (provider.departmentId != null && provider.departmentId != '') {
        deepLink += "&selDepId=" + provider.departmentId;
      }
  
      if (provider.specialtyId != null && provider.specialtyId != '') {
        deepLink += "&selSpecialtyId=" + provider.specialtyId;
      }

      var searchMethod = sessionStorage.getItem('locationSearchMethod') ?? 'unknown';

      if (searchMethod === 'freeText') {
        deepLink += "&linksource=AUTHHMLOCFTDETSPC";
      }
      else if (searchMethod === 'quickSearch') {
        deepLink += "&linksource=AUTHHMLOCQSDETSPC";
      }

      window.location.href = deepLink;
    }
  
    scheduleUnAuth(isMobile: boolean, provider: any) {
      var deepLink = environment.web_open_scheduling_deep_link_base_url 
        + "?id=" + provider.providerEmrId;

      if (provider.departmentId != null && provider.departmentId != '') {
        deepLink += "&dept=" + provider.departmentId;
      }

      if (provider.specialtyId != null && provider.specialtyId != '') {
        deepLink += "&specialty=" + provider.specialtyId;
      }

      if (this.providerDetails !== null) {
        deepLink += "&linksource=PLUAPROFLOCDETPROVCARD";
      }
      else { 
        var searchMethod = sessionStorage.getItem('locationSearchMethod') || 'unknown';

        if (searchMethod === 'freeText') {
          deepLink += "&linksource=PLUALOCFTVDSCHEDPROVCARD";
        }
        else if (searchMethod === 'quickSearch') {
          deepLink += "&linksource=PLUALOCQSVDSCHEDPROVCARD";
        }
        
      }
  
      window.location.href = deepLink;
    }

  viewProvider(provider: any) {
    if(this.providerDetailsLocation == null) {
      console.log('Viewing details for provider:', provider);

      window.scrollTo({ top: 50, behavior: 'smooth' });
      
      this.searchState.setShowSpinner(true);
      
      var providerDetailsRequest = new PostGetProviderDetailsRequest();
      providerDetailsRequest.providerID = provider.providerId;
      providerDetailsRequest.cityOrZip = this.queryCityZip;

      this.mcAppGateway.PostGetProviderDetails(providerDetailsRequest).subscribe({
        next: (response) => {
          console.log('Provider Details Response:', response);
          
          // Handle the response as needed
          this.searchState.setCurrentLocationDetailsProvider(response);
          this.searchState.setShowSpinner(false);
        },
        error: (error) => {
          console.error('Error fetching provider details:', error);
          alert('An error occurred while fetching provider details. Please try again later.');
          this.searchState.setShowSpinner(false);
          this.searchState.clearCurrentLocationDetailsProvider();
        }

      });
    }
    else {
      console.log('Already in Location Details Provider:', provider);
    }
  }
}
